import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { ModalWrapper } from "./chatScreen/chatbar";
import { dispatchContactsChanged } from "../utils/contactListSync";
import { appStorage } from "../utils/appStorage";

export default function Notification() {

    const [notificationsByType, setNotificationsByType] = useState({
        contacts_notification: [],
        message_notification: [],
        profile_notification: [],
    });
    const [activeNotificationTab, setActiveNotificationTab] = useState("contacts_notification");
    const [loading, setLoading] = useState(true);
    const [hasMarkedRead, setHasMarkedRead] = useState(false);

    const [selectedUser, setSelectedUser] = useState(null);
    const [confirmModal, setConfirmModal] = useState(false);
    const [tagModal, setTagModal] = useState(false);

    const [filters] = useState(["Friend", "Family", "Work", "Other"]);
    const [tags, setTags] = useState([]);
    const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 900 : false);
    const [mobileView, setMobileView] = useState("buckets"); // 'buckets' | 'list' | 'detail'
    const [mobileSelectedNotification, setMobileSelectedNotification] = useState(null);

    const formatNotificationDateTime = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";

        const datePart = new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        }).format(date);

        const timePart = new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }).format(date);

        return `${datePart} · ${timePart}`;
    };

    const extractGroupedNotifications = (responseData) => {
        const blank = {
            contacts_notification: [],
            message_notification: [],
            profile_notification: [],
        };

        const containers = [responseData, responseData?.data];
        for (const container of containers) {
            if (!container || typeof container !== "object" || Array.isArray(container)) continue;

            const grouped = {
                contacts_notification: Array.isArray(container.contacts_notification)
                    ? container.contacts_notification
                    : [],
                message_notification: Array.isArray(container.message_notification)
                    ? container.message_notification
                    : [],
                profile_notification: Array.isArray(container.profile_notification)
                    ? container.profile_notification
                    : [],
            };

            const hasAny =
                grouped.contacts_notification.length > 0 ||
                grouped.message_notification.length > 0 ||
                grouped.profile_notification.length > 0;

            if (hasAny) return grouped;
        }

        return blank;
    };

    const flattenNotifications = (grouped) => [
        ...(Array.isArray(grouped?.contacts_notification) ? grouped.contacts_notification : []),
        ...(Array.isArray(grouped?.message_notification) ? grouped.message_notification : []),
        ...(Array.isArray(grouped?.profile_notification) ? grouped.profile_notification : []),
    ];
    // ========================= FETCH LIST ==========================
    useEffect(() => {
        const fetchFriendRequests = async () => {
            try {
                const token = Cookies.get("auth_token");
                if (!token) {
                    toast.error("Session expired. Please log in again.");
                    return (window.location.href = "/login");
                }

                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/notifications`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                const grouped = extractGroupedNotifications(data);
                const list = flattenNotifications(grouped);

                const isExplicitFailure =
                    (data?.status && data.status !== "1") ||
                    (data?.Status && data.Status !== "1");

                if (isExplicitFailure) {
                    setNotificationsByType({
                        contacts_notification: [],
                        message_notification: [],
                        profile_notification: [],
                    });
                    appStorage.setItem("unread_notification_count", "0");
                    appStorage.setItem("has_new_notification", "false");
                } else {
                    setNotificationsByType(grouped);
                    setHasMarkedRead(false);

                    // update unread indicator for sidebar (count only unread)
                    const unreadCount = list.filter(n => !n.read).length;
                    const hasNew = unreadCount > 0;
                    appStorage.setItem("unread_notification_count", unreadCount.toString());
                    appStorage.setItem("has_new_notification", hasNew ? "true" : "false");
                }
            } catch (err) {
                toast.error(err.message || "Failed to load friend requests");
            } finally {
                setLoading(false);
            }
        };

        fetchFriendRequests();
    }, []);

    // Mark notifications as read once on page load after data arrives
    useEffect(() => {
        const mm = window.matchMedia("(max-width: 899px)");
        const onChange = (e) => setIsMobile(e.matches);
        if (mm.addEventListener) mm.addEventListener("change", onChange);
        else mm.addListener(onChange);
        setIsMobile(mm.matches);

        const markRead = async () => {
            const allNotifications = flattenNotifications(notificationsByType);
            if (hasMarkedRead || !allNotifications?.length) return;
            const token = Cookies.get("auth_token");
            if (!token) return;

            const ids = Array.from(
                new Set(
                    allNotifications
                        ?.filter((n) => !n.read)
                        ?.map((n) => n.notification_id || n.id)
                        ?.filter(Boolean)
                )
            );

            if (!ids.length) return;

            try {
                await Promise.all(
                    ids.map(async (id) => {
                        const body = new URLSearchParams();
                        body.append("notification_id", id);
                        const res = await fetch(
                            `${process.env.REACT_APP_API_BASE_URL}/notifications/mark-read`,
                            {
                                method: "POST",
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                    "Content-Type": "application/x-www-form-urlencoded",
                                },
                                body: body.toString(),
                            }
                        );
                        const data = await res.json();
                        if (data.status !== "1" && data.Status !== "1") {
                            throw new Error(data.message || "Failed to mark read");
                        }
                    })
                );
                setHasMarkedRead(true);

                // clear unread indicators once marked read
                appStorage.setItem("unread_notification_count", "0");
                appStorage.setItem("has_new_notification", "false");
            } catch (err) {
                }
        };

        markRead();
    }, [notificationsByType, hasMarkedRead]);

    // ========================= DISMISS NOTIFICATION ==========================
    const dismissNotification = async (notificationId) => {
        if (!notificationId) return;
        const token = Cookies.get("auth_token");
        if (!token) return;

        const body = new URLSearchParams();
        body.append("notification_id", notificationId);

        const res = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/notifications/dismiss`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: body.toString(),
            }
        );

        const data = await res.json();
        if (data.status !== "1" && data.Status !== "1") {
            throw new Error(data.message || "Failed to dismiss notification");
        }
    };

    // ========================= SUBMIT ACTION ==========================
    const handleSubmitAction = async (actionType, targetUser) => {
        try {
            const target = targetUser || selectedUser;
            if (!target?.friend_user_id) {
                toast.error("Invalid notification data");
                return;
            }

            const token = Cookies.get("auth_token");
            if (!token) return toast.error("Session expired");

            const endpoint = "/friend-request/accept_reject_friend_request";

            const payload = new URLSearchParams({
                user_id: target.friend_user_id,
                action: actionType,   // 1 = accept, 3 = reject
            });

            // Add tags only when user accepted
            if (actionType === "1") {
                if (tags.length === 0) {
                    return toast.error("Please select at least one tag");
                }
                const relationsToSend = tags.length > 0
                    ? [...tags].join(",")
                    : "";
                payload.append("relations", relationsToSend);
            }

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Authorization: `Bearer ${token}`,
                    },
                    body: payload.toString(),
                }
            );

            const data = await res.json();

            if (data.status === "1") {
                toast.success(data.message || "Success");

                // Dismiss the corresponding notification if present
                try {
                    if (target?.notification_id) {
                        await dismissNotification(target.notification_id);
                    }
                } catch (e) {
                    toast.error(e.message || "Failed to dismiss notification");
                }

                // Remove from all buckets by notification id
                setNotificationsByType((prev) => {
                    const removeId = String(target?.notification_id || "");
                    if (!removeId) return prev;
                    return {
                        contacts_notification: (prev.contacts_notification || []).filter(
                            (r) => String(r.notification_id || r.id) !== removeId
                        ),
                        message_notification: (prev.message_notification || []).filter(
                            (r) => String(r.notification_id || r.id) !== removeId
                        ),
                        profile_notification: (prev.profile_notification || []).filter(
                            (r) => String(r.notification_id || r.id) !== removeId
                        ),
                    };
                });

                // Close modals
                setConfirmModal(false);
                setTagModal(false);
                setTags([]);
                dispatchContactsChanged({
                    userId: target.friend_user_id,
                    action: actionType === "1" ? "friend_accepted" : "friend_rejected",
                });
            } else {
                toast.error(data.message || "Failed to update request");
            }
        } catch (err) {
            toast.error(err.message || "Error processing request");
        }
    };

    // ========================= HANDLE NOTIFICATION CLICK ==========================
    const handleNotificationClick = (notification) => {
        // Redirect to chat if notification is an individual message
        if (notification.tag === "individual_message" || notification.type === "individual_message") {
            // Use friend_user_id for chat route
            if (notification.friend_user_id) {
                window.location.href = `/chat/${notification.friend_user_id}`;
                return;
            }
        }
        // Redirect to group chat if notification is a group message
        if (notification.tag === "group_message" || notification.type === "group_message") {
            const groupId = notification.group_id || notification.friend_user_id;
            if (groupId) {
                window.location.href = `/groupchat?group_id=${groupId}`;
                return;
            }
        }
        if (notification.type === "select_relation" || notification.tag === "select_relation") {
            setSelectedUser(notification);
            setConfirmModal(false);
            setTagModal(true);
        }
    };

  
    // ========================= FRIEND STATUS HELPERS ==========================
    const isPendingFriendRequest = (n) => {
        if (!n || n.type !== "friend_request") return false;
        const s = n.friend_status;
        if (s === undefined || s === null) return true; // treat missing as pending
        const num = typeof s === "string" ? parseInt(s, 10) : s;
        return Number.isNaN(num) ? true : num === 0; // 0 or NaN => pending
    };

    const friendStatusText = (n) => {
        const s = typeof n?.friend_status === "string" ? parseInt(n.friend_status, 10) : n?.friend_status;
        switch (s) {
            case 1:
                return "Accepted";
            case 2:
                return "Removed";
            case 3:
                return "Declined";
            default:
                return null;
        }
    };
    const notificationBuckets = [
        { key: "contacts_notification", label: "Connections ", icon: "../chatbar/connect.svg" },
        { key: "message_notification", label: "Messages", icon: "../chatbar/chat.svg" },
        { key: "profile_notification", label: "Profile", icon: "../chatbar/profile.svg" },
    ];

    const activeNotificationList = Array.isArray(notificationsByType?.[activeNotificationTab])
        ? notificationsByType[activeNotificationTab]
        : [];

    const toggleTagSelection = (tag) => {
        setTags((prevTags) => {
            if (tag === "Other") {
                return prevTags.includes("Other") ? [] : ["Other"];
            }

            const nextTags = prevTags.filter((value) => value !== "Other");
            return nextTags.includes(tag)
                ? nextTags.filter((value) => value !== tag)
                : [...nextTags, tag];
        });
    };

    // ========================= RENDER UI ==========================
    if (loading) {
        return (
            <div className="h-full min-h-0 overflow-hidden bg-white">
                <div className="flex h-full min-h-0">
                    {/* Left skeleton tabs */}
                    <div className="w-full md:w-[360px] md:min-w-[300px] md:flex-shrink-0 border-r border-gray-100 overflow-hidden flex min-h-0 flex-col bg-white">
                        <div className="px-4 py-6 border-b border-gray-100">
                            <div className="flex items-center justify-between animate-pulse">
                                <div className="h-6 w-32 rounded-lg bg-gray-200" />
                                <div className="h-6 w-10 rounded-full bg-gray-200" />
                            </div>
                        </div>
                        <div
                            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 animate-pulse
                            [&::-webkit-scrollbar]:w-1.5
                            [&::-webkit-scrollbar-thumb]:bg-gray-300
                            [&::-webkit-scrollbar-thumb]:rounded-full
                            [&::-webkit-scrollbar-track]:bg-transparent"
                        >
                            {[...Array(3)].map((_, idx) => (
                                <div
                                    key={idx}
                                    className="p-3 bg-white rounded-[16px] border border-gray-100 shadow-sm flex items-center gap-3"
                                >
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-gray-200 rounded-lg w-3/4" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right placeholder */}
                    <div className="hidden md:flex md:flex-1 items-center justify-center bg-[#F3F0FF] relative overflow-hidden min-w-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#F3F0FF] via-white to-[#EDE7FF]" />
                        <div className="relative flex flex-col items-center gap-3 animate-pulse">
                            <div className="h-16 w-16 rounded-2xl bg-white shadow-[0_10px_50px_rgba(105,56,239,0.1)]" />
                            <div className="h-4 w-40 rounded-lg bg-white/60" />
                            <div className="h-3 w-52 rounded-lg bg-white/40" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full min-h-0 overflow-hidden bg-white">
            <div className="flex h-full min-h-0">
                <div className={`${isMobile && mobileView !== 'buckets' ? 'hidden' : ''} w-full lg:w-[360px] md:min-w-[300px] md:flex-shrink-0 border-r border-gray-100 overflow-hidden flex min-h-0 flex-col bg-white`}>
                    <div className="px-4 py-6 border-b border-gray-100 bg-white">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
                        </div>
                    </div>

                    <div className="px-4 py-4 space-y-3">
                        {notificationBuckets.map((bucket) => {
                            const active = activeNotificationTab === bucket.key;
                            return (
                                <button
                                    key={bucket.key}
                                    type="button"
                                    onClick={() => {
                                        setActiveNotificationTab(bucket.key);
                                        setMobileSelectedNotification(null);
                                        if (isMobile) setMobileView('list');
                                    }}
                                    className={`w-full flex items-center justify-between rounded-2xl border px-4 py-3 transition ${
                                        active
                                            ? "bg-[#F3EEFF] border-[#7C4DFF] text-[#5D35D6]"
                                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                                    }`}
                                >
                                    <span className="flex items-center gap-3 font-semibold">
                                        <img src={bucket.icon} alt={bucket.label} className="w-5 h-5" />
                                        {bucket.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT SIDE SELECTED TAB LIST */}
                <div className={`${isMobile && mobileView === 'buckets' ? 'hidden' : ''} w-full md:flex-1 min-w-0 min-h-0 flex flex-col bg-[#F3F0FF]`}>
                    <div className="px-4 md:px-6 py-4 border-b border-[#E4DCF9] bg-[#F3F0FF]">
                        <div className="flex items-center gap-3">
                            {isMobile && mobileView === 'list' && (
                                <button type="button" onClick={() => setMobileView('buckets')} className="px-3 py-2 rounded-lg bg-white border border-gray-200">
                                    ← Back
                                </button>
                            )}
                            {isMobile && mobileView === 'detail' && (
                                <button type="button" onClick={() => setMobileView('list')} className="px-3 py-2 rounded-lg bg-white border border-gray-200">
                                    ← Back
                                </button>
                            )}
                            <h2 className="text-lg font-bold text-[#312A5E]">
                                {notificationBuckets.find((b) => b.key === activeNotificationTab)?.label || "Notifications"}
                            </h2>
                        </div>
                    </div>

                    <div
                        className="flex-1 overflow-y-auto px-4 md:px-6 py-4
                        [&::-webkit-scrollbar]:w-1.5
                        [&::-webkit-scrollbar-thumb]:bg-gray-400
                        [&::-webkit-scrollbar-thumb]:rounded-full
                        [&::-webkit-scrollbar-track]:bg-transparent"
                    >
                        {mobileView === 'detail' && isMobile && mobileSelectedNotification ? (
                            <div className="p-4">
                                <div className="cursor-default flex items-start gap-3 p-3 bg-white rounded-[18px] border border-[#E3DFF4] shadow-sm relative mb-4">
                                    <img
                                        src={mobileSelectedNotification.profile_image
                                            ? `${process.env.REACT_APP_API_BASE_URL}${mobileSelectedNotification.profile_image}`
                                            : "/default-avatar.png"}
                                        alt={mobileSelectedNotification.full_name}
                                        className="h-12 w-12 rounded-full object-cover flex-shrink-0 ring-3 ring-gray-100"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="body-font text-[13px] text-gray-600">
                                            {mobileSelectedNotification.message}
                                        </p>
                                        {formatNotificationDateTime(mobileSelectedNotification.created_at) && (
                                            <p className="mt-1 text-[13px] text-gray-500">
                                                {formatNotificationDateTime(mobileSelectedNotification.created_at)}
                                            </p>
                                        )}

                                        {(mobileSelectedNotification.type === "friend_request" || mobileSelectedNotification.tag === "friend_request") && (
                                            isPendingFriendRequest(mobileSelectedNotification) ? (
                                                <div className="flex gap-2 mt-3">
                                                    <button
                                                        onClick={() => handleSubmitAction("3", mobileSelectedNotification)}
                                                        className="flex-1 px-3 py-1.5 text-xs font-medium border border-[#7544FC] text-[#7544FC] rounded-lg hover:bg-purple-50 transition-colors"
                                                    >
                                                        Decline
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedUser(mobileSelectedNotification); setTagModal(true); }}
                                                        className="flex-1 text-xs font-medium submit-button !py-1.5 !px-3 rounded-lg"
                                                    >
                                                        Accept
                                                    </button>
                                                </div>
                                            ) : (
                                                friendStatusText(mobileSelectedNotification) && (
                                                    <div className="flex mt-3">
                                                        <span className="px-2 py-1 text-xs rounded-lg bg-gray-100 text-gray-600 border border-gray-200 font-medium">
                                                            {friendStatusText(mobileSelectedNotification)}
                                                        </span>
                                                    </div>
                                                )
                                            )
                                        )}
                                        <div className="mt-3">
                                            <button className="px-3 py-2 rounded-lg border" onClick={() => handleNotificationClick(mobileSelectedNotification)}>
                                                Open
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : activeNotificationList.length === 0 ? (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center px-6 py-8">
                                    <div className="h-16 w-16 mx-auto rounded-full bg-gray-200 mb-4 flex items-center justify-center">
                                        <span className="text-2xl">📭</span>
                                    </div>
                                    <p className="text-gray-600 font-medium">No notifications in this tab</p>
                                    <p className="text-gray-400 text-sm mt-1">Switch tab to view other notifications</p>
                                </div>
                            </div>
                        ) : (
                            activeNotificationList.map((req, i) => (
                                <div key={req.e_id || req.id || i} onClick={(e) => {
                                    if (isMobile) {
                                        e.stopPropagation();
                                        setMobileSelectedNotification(req);
                                        setMobileView('detail');
                                    } else {
                                        handleNotificationClick(req);
                                    }
                                }} className="cursor-pointer flex items-center gap-3 p-2 bg-white rounded-[18px] border border-[#E3DFF4] shadow-sm relative mb-4" >
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={req.profile_image
                                                ? `${process.env.REACT_APP_API_BASE_URL}${req.profile_image}`
                                                : "/default-avatar.png"}
                                            alt={req.full_name}
                                            className="h-12 w-12 rounded-full object-cover flex-shrink-0 ring-3 ring-gray-100"
                                            onError={(e) => {
                                                e.currentTarget.src = "/default-avatar.png";
                                            }}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="body-font text-[13px] text-gray-600 line-clamp-2">
                                                {req.message}
                                            </p>
                                            {formatNotificationDateTime(req.created_at) && (
                                                <p className="mt-1 text-[13px] text-gray-500">
                                                    {formatNotificationDateTime(req.created_at)}
                                                </p>
                                            )}

                                            {/* Friend request actions or status */}
                                            {(req.type === "friend_request" || req.tag === "friend_request") && (
                                                isPendingFriendRequest(req) ? (
                                                    <div className="flex gap-2 mt-3">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSubmitAction("3", req);
                                                            }}
                                                            className="flex-1 px-3 py-1.5 text-xs font-medium border border-[#7544FC] text-[#7544FC] rounded-lg hover:bg-purple-50 transition-colors"
                                                        >
                                                            Decline
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedUser(req);
                                                                setConfirmModal(false);
                                                                setTagModal(true);
                                                            }}
                                                            className="flex-1 text-xs font-medium submit-button !py-1.5 !px-3 rounded-lg"
                                                        >
                                                            Accept
                                                        </button>
                                                    </div>
                                                ) : (
                                                    friendStatusText(req) && (
                                                        <div className="flex">
                                                            <span className="px-2 py-1 text-xs rounded-lg bg-gray-100 text-gray-600 border border-gray-200 font-medium">
                                                                {friendStatusText(req)}
                                                            </span>
                                                        </div>
                                                    )
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* CONFIRM MODAL */}
            {confirmModal && selectedUser && (
                <ModalWrapper onClose={() => setConfirmModal(false)}>
                    <p className="mb-5 text-lg font-semibold">
                        Accept {selectedUser.full_name}'s request?
                    </p>

                    <button
                        className="w-full px-[30px] py-[12px] border border-purple-600 rounded-[25px]"
                        onClick={() => handleSubmitAction("3")} // Reject action
                    >
                        Decline
                    </button>

                    <button
                        className="w-full submit-button"
                        onClick={() => {
                            setConfirmModal(false);
                            setTagModal(true); // Open tag modal
                        }}
                    >
                        Accept
                    </button>
                </ModalWrapper>
            )}

            {/* TAG MODAL */}
            {tagModal && selectedUser && (
                <ModalWrapper onClose={() => setTagModal(false)}>
                    <p className="text-lg font-semibold mb-4">
                        Please select how do you know {selectedUser.full_name || selectedUser.related_user_name || selectedUser.name}
                    </p>

                    <div className="gap-3 flex flex-col">
                        {filters.map((tag, index) => {
                            const isSelected = tags.includes(tag);
                            return (
                                <div
                                    key={index}
                                    onClick={() => toggleTagSelection(tag)}
                                    className={`flex items-center justify-between p-3 rounded-[100px] border shadow-md cursor-pointer 
                        ${isSelected ? "bg-[#EDE7FF] border-[#7C4DFF]" : "bg-[#F3F0F5] border-gray-200"}`}
                                >
                                    <p className="font-semibold text-sm">{tag}</p>

                                    <div
                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
                        ${isSelected ? "bg-[#7C4DFF] border-[#7C4DFF]" : "border-gray-400"}`}
                                    >
                                        {isSelected && (
                                            <img
                                                src="../chatscreen/radiobutton.svg"
                                                alt="Selected"
                                                className="w-6 h-6"
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <button
                        className="w-full submit-button mt-4"
                        onClick={() => handleSubmitAction("1")} // Accept + Tags
                    >
                        Submit
                    </button>

                    <button
                        className="w-full normal-button mt-3"
                        onClick={() => setTagModal(false)}
                    >
                        Cancel
                    </button>
                </ModalWrapper>
            )}
        </div>
    );
}
